// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

const LoginPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptAge, setAcceptAge] = useState(false);
  const [acceptTos, setAcceptTos] = useState(false);
  const [showError, setShowError] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const canSubmit = acceptAge && acceptTos;

  const handleGoogleLogin = () => {
    if (!canSubmit) {
      setShowError(true);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 bg-[#1C1C1A]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="sapiens-card p-8 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 text-[#888780] hover:text-[#E8E8E4] transition-colors p-1 rounded-lg hover:bg-white/[0.06]"
            aria-label="Chiudi"
            data-testid="login-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="w-3 h-3 rounded-full bg-[#C9A84C]"></span>
              <span className="text-[#E8E8E4] text-2xl font-medium">Sapiens</span>
            </div>
            <h1 className="text-2xl font-medium text-[#E8E8E4] mb-2">Benvenuto</h1>
            <p className="text-[#888780]">
              Accedi per salvare i tuoi progressi e sbloccare tutte le funzionalità
            </p>
          </div>

          <div className="space-y-4">
            {/* Mandatory consents — Deployment 12 GDPR/ToS compliance */}
            <div className="space-y-3 p-4 rounded-xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="login-consents">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptAge}
                  onChange={e => { setAcceptAge(e.target.checked); setShowError(false); }}
                  className="mt-1 w-4 h-4 accent-[#C9A84C] flex-shrink-0"
                  data-testid="login-checkbox-age"
                  required
                />
                <span className="text-xs text-[#888780] leading-relaxed">
                  Dichiaro di avere <strong className="text-[#E8E8E4]">almeno 18 anni</strong> (Art. 2 BIS dei <Link to="/termini" className="text-[#C9A84C] underline">Termini di Servizio</Link>).
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTos}
                  onChange={e => { setAcceptTos(e.target.checked); setShowError(false); }}
                  className="mt-1 w-4 h-4 accent-[#C9A84C] flex-shrink-0"
                  data-testid="login-checkbox-tos"
                  required
                />
                <span className="text-xs text-[#888780] leading-relaxed">
                  Accetto i <Link to="/termini" className="text-[#C9A84C] underline">Termini di Servizio</Link> e ho letto la <Link to="/privacy" className="text-[#C9A84C] underline">Privacy Policy</Link>.
                </span>
              </label>
              {showError && !canSubmit && (
                <p className="text-xs text-[#F08080]" data-testid="login-consent-error">⚠ Devi accettare entrambe le condizioni per continuare.</p>
              )}
            </div>

            <Button
              onClick={handleGoogleLogin}
              disabled={!canSubmit}
              className="w-full h-12 sapiens-btn-primary text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continua con Google
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-[#888780]">
              Continuando, accetti i nostri{' '}
              <Link to="/termini" className="text-[#C9A84C] hover:underline">Termini di servizio</Link>
              {' '}e la{' '}
              <Link to="/privacy" className="text-[#C9A84C] hover:underline">Privacy Policy</Link>
            </p>
          </div>

          <div className="mt-6 p-4 bg-[#2A4A35]/30 rounded-xl border border-[#3D7A52]/30">
            <div className="flex items-start gap-3">
              <span className="text-xl">🎁</span>
              <div>
                <h3 className="text-[#E8E8E4] font-medium mb-1">Account gratuito include:</h3>
                <ul className="text-sm text-[#888780] space-y-1">
                  <li>• Livello 1 e 2 completi</li>
                  <li>• 3 quiz al giorno</li>
                  <li>• Calcolatori base</li>
                  <li>• Glossario completo</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
